import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import {
  AnnouncementPanel,
  Grounder,
  HeadingSection,
} from '@innovamat/glimmer-components';

import {
  AddClassroomModal,
  CourseBlock,
  ERROR_TYPE,
  ErrorPage,
  useNavigation,
} from '../../..';
import {
  UpdatePeriodAnnouncementPanel,
  useCheckIsUpdatePeriod,
} from '../../../components/update-period-announcement-panel';
import { ConfirmationModal } from '../../components/confirmation-modal';
import { useManageClassrooms } from '../../hooks/use-manage-classrooms';
import {
  SchoolJourneysEmptyState,
  SchoolJourneysList,
  SchoolJourneysSkeleton,
} from './components';
import { SchoolJourneysHeader } from './components/school-journeys-header/school-journeys-header';
import { useSchoolJourneysData } from './hooks';
import { useSchoolJourneyCreateOptions } from './hooks/school-journey-create-options/use-school-journey-create-options';

const ListContainer = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const JourneyGrid = styled.div`
  display: grid;
  gap: 16px;
  width: 100%;
  grid-template-columns: 1fr 1fr 1fr 1fr;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.xl}) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const BlockedJourneys = styled('div')<{ isDisabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ isDisabled }) =>
    isDisabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
      filter: grayscale(1);
    `}
`;

const Separator = styled.div`
  margin-top: 40px;
`;

const EmptyStateWrapper = styled.div`
  margin-top: 56px;
`;

const UncheckedClassesAfterYearUpgradeAnnouncementPanel = styled(
  AnnouncementPanel
)`
  margin-top: 16px;
`;

function SchoolJourneys(): JSX.Element {
  const { t } = useTranslation();

  const {
    handleDeleteClassWithConfirmation,
    confirmModalData,
    editModalData,
    openEditModal,
  } = useManageClassrooms();

  const { goToSchoolClassroom } = useNavigation();

  const isUpdatePeriod = useCheckIsUpdatePeriod();

  const {
    journeys,
    hasUncheckedJourneys,
    isEmpty,
    isError,
    isLoading,
    isSuccess,
  } = useSchoolJourneysData();

  const { createJourneyOptions } = useSchoolJourneyCreateOptions({
    openEditModal,
    handleDeleteClassWithConfirmation,
  });

  const handleNavigateToClassroom = (classroomId: string): void => {
    goToSchoolClassroom(classroomId);
  };

  return (
    <>
      <HeadingSection
        title={t('sidebar.items.journeys-list')}
        actions={<SchoolJourneysHeader />}
      />

      <Separator />

      {hasUncheckedJourneys && isSuccess && !isUpdatePeriod && (
        <UncheckedClassesAfterYearUpgradeAnnouncementPanel
          text={t('classrooms.updatePeriod.announcement')}
          canClose={false}
          type="imported"
        />
      )}

      <UpdatePeriodAnnouncementPanel />

      <ListContainer>
        {isLoading && <SchoolJourneysSkeleton />}
        {isError && <ErrorPage errorType={ERROR_TYPE.ERROR_500} />}
        {isEmpty ? (
          <EmptyStateWrapper>
            <SchoolJourneysEmptyState />
          </EmptyStateWrapper>
        ) : (
          <BlockedJourneys isDisabled={isUpdatePeriod}>
            <SchoolJourneysList
              journeys={journeys}
              isUpdatePeriod={isUpdatePeriod}
              createJourneyOptions={createJourneyOptions}
              onNavigateToClassroom={handleNavigateToClassroom}
            />
          </BlockedJourneys>
        )}

        <Grounder />
      </ListContainer>
      <ConfirmationModal {...confirmModalData} />
      <AddClassroomModal {...editModalData} />
    </>
  );
}

export { CourseBlock, JourneyGrid, ListContainer, SchoolJourneys };
